import React, { useEffect, useState } from "react";
import {
  HiOutlineDocumentText,
  HiOutlineReceiptPercent,
  HiOutlineInboxStack,
} from "react-icons/hi2";
import { billingApi } from "../../../api";
import {
  IPaymentHistoryRecord,
  IResponseError,
} from "../../../data-access";
import "../../../ui/simple-table";
import { DataTablePagination } from "../../../ui/data-table/data-table-pagination/data-table-pagination";
import { paginate } from "../../../ui/data-table/data-table-utils";
import { capitalize, handleFailedRequest } from "../../../util";
import "./payment-history.scss";
import moment from "moment";

export const PaymentHistory = (): JSX.Element => {
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [paymentHistory, setPaymentHistory] = useState<IPaymentHistoryRecord[]>(
    []
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set([0]));

  const toggleRow = (index: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const openPaymentDetails = (url: string) => {
    window.open(url, "_blank");
  };

  const getStatusBadge = (status: string): JSX.Element => {
    const label = capitalize(status.replace("_", " "));
    const className =
      status === "paid"
        ? "payment-status--paid"
        : "payment-status--failed";
    return <span className={`payment-status ${className}`}>{label}</span>;
  };

  const handlePagination = (pp: number, requestedPage: number): void => {
    const paginateData = paginate(paymentHistory, pp, requestedPage);
    const actualPage =
      paginateData.length > 0
        ? requestedPage
        : Math.ceil(paymentHistory.length / pp);
    setPerPage(pp);
    setCurrentPage(actualPage);
    setExpandedRows(new Set([0]));
  };

  useEffect(() => {
    billingApi
      .getPaymentsHistory()
      .then(
        (response: {
          data: {
            billingPayments: IPaymentHistoryRecord[];
            billingBotPayments: IPaymentHistoryRecord[];
          };
        }) => {
          const paymentsData = [
            ...response.data.billingPayments,
            ...response.data.billingBotPayments,
          ];
          const sortedData = paymentsData.sort(
            (a: IPaymentHistoryRecord, b: IPaymentHistoryRecord) =>
              moment(b.order_time).unix() - moment(a.order_time).unix()
          );
          setPaymentHistory(sortedData);
        }
      )
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, []);

  // ── Shimmer ──

  const renderShimmer = () => (
    <div className="simple-table payment-history__table">
      <div className="simple-table__header payment-history__grid">
        <span className="simple-table__col">Date</span>
        <span className="simple-table__col">Plan</span>
        <span className="simple-table__col">Status</span>
        <span className="simple-table__col">Card</span>
        <span className="simple-table__col">Amount</span>
        <span className="simple-table__col simple-table__col--right">Invoice</span>
      </div>
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="simple-table__row simple-table__row--expanded payment-history__grid">
          <span className="simple-table__col" data-label="Date"><div className="payment-history__shimmer payment-history__shimmer--date" /></span>
          <span className="simple-table__col" data-label="Plan"><div className="payment-history__shimmer payment-history__shimmer--plan" /></span>
          <span className="simple-table__col" data-label="Status"><div className="payment-history__shimmer payment-history__shimmer--badge" /></span>
          <span className="simple-table__col" data-label="Card"><div className="payment-history__shimmer payment-history__shimmer--card" /></span>
          <span className="simple-table__col" data-label="Amount"><div className="payment-history__shimmer payment-history__shimmer--amount" /></span>
          <span className="simple-table__col simple-table__col--right" data-label="Invoice"><div className="payment-history__shimmer payment-history__shimmer--btn" /></span>
        </div>
      ))}
    </div>
  );

  // ── Render ──

  const paginatedData = paginate(paymentHistory, perPage, currentPage);

  return (
    <div className="payment-history">
      <h3 className="billing-section-title">Payment History</h3>
      {isFetching ? (
        renderShimmer()
      ) : paymentHistory.length === 0 ? (
        <div className="payment-history__empty">
          <HiOutlineReceiptPercent size={40} />
          <h4>No payments yet</h4>
          <p>Your payment history will appear here</p>
        </div>
      ) : (
        <div className="simple-table payment-history__table">
          <div className="simple-table__header payment-history__grid">
            <span className="simple-table__col">Date</span>
            <span className="simple-table__col">Plan</span>
            <span className="simple-table__col">Status</span>
            <span className="simple-table__col">Card</span>
            <span className="simple-table__col">Amount</span>
            <span className="simple-table__col simple-table__col--right">Invoice</span>
          </div>
          {paginatedData.map((payment, index) => (
            <div
              key={index}
              className={`simple-table__row payment-history__grid${expandedRows.has(index) ? " simple-table__row--expanded" : ""}`}
              onClick={(e) => {
                const header = e.currentTarget.firstElementChild;
                if (header && header.contains(e.target as Node)) toggleRow(index);
              }}
            >
              <span className="simple-table__col simple-table__col--primary" data-label="Date">
                {payment.order_time
                  ? moment(payment.order_time).format("DD MMM YYYY")
                  : "—"}
              </span>
              <span className="simple-table__col" data-label="Plan">
                {payment.billing_plan_name || "—"}
              </span>
              <span className="simple-table__col" data-label="Status">
                {getStatusBadge(payment.status)}
              </span>
              <span className="simple-table__col" data-label="Card">
                {payment.masked_card || <span className="no-data">—</span>}
              </span>
              <span className="simple-table__col simple-table__col--primary" data-label="Amount">
                {payment.amount ? (
                  <span className="payment-amount">${(+payment.amount / 100).toFixed(2)}</span>
                ) : (
                  <span className="no-data">—</span>
                )}
              </span>
              <span className="simple-table__col simple-table__col--right" data-label="Invoice">
                {payment.status !== "failed" && payment.stripe_details_url ? (
                  <button
                    className="invoice-btn"
                    onClick={() => openPaymentDetails(payment.stripe_details_url)}
                  >
                    <HiOutlineDocumentText size={14} />
                    Invoice
                  </button>
                ) : (
                  <span className="no-data">—</span>
                )}
              </span>
            </div>
          ))}
          <DataTablePagination
            perPage={perPage}
            perPageOptions={[10, 25, 50]}
            total={paymentHistory.length}
            currentPage={currentPage}
            onPagination={handlePagination}
          />
        </div>
      )}
    </div>
  );
};
